import { useState } from "react";
import { NavLink, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Loader2 } from "lucide-react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { getDashboardMenuItems } from "./DashboardMenuItems";
import { useStoreProfile } from "@/hooks/useStoreProfile";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: LucideIcon;
  disabled?: boolean;
  external?: boolean;
}

interface NavItemProps {
  href: string;
  icon: LucideIcon;
  children: React.ReactNode;
  disabled?: boolean;
  external?: boolean;
}

const NavItem = ({ href, icon: Icon, children, disabled, external }: NavItemProps) => {
  const location = useLocation();
  const isActive = location.pathname === href;

  if (disabled) {
    return (
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium opacity-50 cursor-not-allowed",
          isActive ? "bg-accent" : "hover:bg-accent/50"
        )}
      >
        <Icon className="h-4 w-4" />
        {children}
      </div>
    );
  }

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          "text-gray-700 hover:bg-[#967F81]/5 hover:text-brand-primary"
        )}
      >
        <Icon className="h-4 w-4 text-gray-500" />
        {children}
      </a>
    );
  }

  return (
    <NavLink
      to={href}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          isActive 
            ? "bg-[#967F81]/10 text-brand-primary font-semibold" 
            : "text-gray-700 hover:bg-[#967F81]/5 hover:text-brand-primary"
        )
      }
    >
      <Icon className={cn("h-4 w-4", isActive ? "text-brand-primary" : "text-gray-500")} />
      {children}
    </NavLink>
  );
};

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [open, setOpen] = useState(false);
  const { user, isFlorist, loading: authLoading } = useAuth();
  const { storeProfile } = useStoreProfile();
  const location = useLocation();

  if (authLoading) {
    return (
      <div className="flex min-h-screen bg-brand-accent items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!user || !isFlorist) {
    return <Navigate to="/" replace />;
  }

  const menuItems = getDashboardMenuItems();
  
  const navigation: NavigationItem[] = menuItems.map(item => ({
    name: item.label,
    href: item.path === "/florist" && storeProfile ? `/florist/${encodeURIComponent(storeProfile.id)}` : item.path,
    icon: item.icon,
    disabled: item.path === "/florist" && !storeProfile,
    external: item.path === "/florist" && !!storeProfile
  }));

  return (
    <div className="flex min-h-screen bg-brand-accent">
      {/* Mobile navigation trigger */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            className="lg:hidden fixed left-4 top-28 z-40 bg-white/50 hover:bg-white"
            size="icon"
          >
            <Menu className="h-5 w-5 text-brand-primary" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[240px] p-0 bg-white border-r border-brand-primary/10 pt-28">
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <SheetDescription className="sr-only">Florist dashboard navigation options</SheetDescription>
          <div className="py-6 px-4 border-b border-brand-primary/10">
            <h2 className="text-xl font-semibold text-brand-primary">{storeProfile?.store_name || "My Store"}</h2>
            <p className="text-sm text-brand-primary/70">Florist Dashboard</p>
          </div>
          <nav className="flex flex-col gap-1 p-4">
            {navigation.map((item) => (
              <NavItem
                key={item.name}
                href={item.href}
                icon={item.icon}
                disabled={item.disabled}
                external={item.external}
              >
                {item.name}
              </NavItem>
            ))}
          </nav>
        </SheetContent>
      </Sheet>

      {/* Desktop navigation */}
      <nav className="hidden lg:flex flex-col gap-1 w-[240px] p-0 bg-white border-r border-brand-primary/10 min-h-screen">
        <div className="py-6 px-4 border-b border-brand-primary/10">
          <h2 className="text-xl font-semibold text-brand-primary">{storeProfile?.store_name || "My Store"}</h2>
          <p className="text-sm text-brand-primary/70">Florist Dashboard</p>
        </div>
        <div className="flex flex-col gap-1 p-4">
          {navigation.map((item) => (
            <NavItem
              key={item.name}
              href={item.href}
              icon={item.icon}
              disabled={item.disabled}
              external={item.external}
            >
              {item.name}
            </NavItem>
          ))}
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1 pt-44 pb-8 px-8 lg:pt-20">
        <div className="relative max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-brand-primary/5 p-6">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}